import React, { useContext } from "react"
import { Controller, useFormContext } from "react-hook-form"

import AlertBanner from "src/components/AlertBanner"
import Form from "src/components/Form"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

import { PAYMENT_METHODS, VALIDATED_FIELDS_BY_TAB } from "../../constants"
import { ContractsFormContext } from "../ContractsFormContext"
import PageActions from "../wizard/PageActions"

const PaymentMethods = () => {
  const marinaSlug = getCurrentMarinaSlug()
  const { surchargeCopy, surchargeRemovedButEnabled } =
    useContext(ContractsFormContext)
  const {
    register,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()
  const [applySurcharge, paymentMethods] = watch([
    "applySurcharge",
    "acceptedPaymentMethods",
  ])

  const handleChange = (method) => {
    const checkingCardOption =
      method === PAYMENT_METHODS.card && !paymentMethods.includes(method)
    const uncheckingCardOption =
      method === PAYMENT_METHODS.card && paymentMethods.includes(method)

    if (surchargeCopy && checkingCardOption) {
      setValue("applySurcharge", true)
    } else if (uncheckingCardOption) {
      setValue("applySurcharge", false)
    }

    if (paymentMethods.includes(method)) {
      return paymentMethods.filter((m) => m !== method)
    }
    return [...paymentMethods, method]
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Form.Label htmlFor="payment-methods">Payment Methods</Form.Label>
          <p className="text-muted pb-2">
            Select the payment method(s) your customers can use for this
            contract. At least one must be selected.
          </p>
          <Controller
            name="acceptedPaymentMethods"
            render={({ field: { onChange, ref } }) => {
              return (
                <div className="flex flex-col">
                  <Form.Checkbox
                    ref={ref}
                    name="paymentMethods-us_bank_account"
                    checked={paymentMethods.includes(PAYMENT_METHODS.bank)}
                    value={PAYMENT_METHODS.bank}
                    onChange={() =>
                      onChange(handleChange(PAYMENT_METHODS.bank))
                    }
                    label="Bank Account/ACH"
                  />
                  <Form.Checkbox
                    ref={ref}
                    name="paymentMethods-card"
                    checked={paymentMethods.includes(PAYMENT_METHODS.card)}
                    value={PAYMENT_METHODS.card}
                    onChange={() =>
                      onChange(handleChange(PAYMENT_METHODS.card))
                    }
                    label="Cards (Credit, Debit, Prepaid)"
                  />
                </div>
              )
            }}
          />
          {surchargeCopy || surchargeRemovedButEnabled ? (
            <div className="ml-4 flex flex-col gap-2">
              <Form.Checkbox
                id="applySurcharge"
                hasErrors={Boolean(errors?.applySurcharge)}
                {...register("applySurcharge")}
                disabled={!paymentMethods.includes(PAYMENT_METHODS.card)}
                label="Apply credit card surcharge"
              />
              {surchargeCopy && applySurcharge ? (
                <AlertBanner type="info">{surchargeCopy}</AlertBanner>
              ) : null}
              {surchargeRemovedButEnabled && applySurcharge ? (
                <AlertBanner type="warning">
                  Notice: You have previously opted to apply a surcharge when
                  processing credit card payments but do not presently have a
                  surcharge configured.{" "}
                  <span className="font-semibold">
                    No fee will be charged unless a surcharge is configured{" "}
                  </span>
                  — please visit{" "}
                  <a
                    href={`/manage/${marinaSlug}/settings`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-blue-700"
                  >
                    settings
                  </a>{" "}
                  to configure one or contact Dockwa support at
                  mayday@dockwa.com for assistance.
                </AlertBanner>
              ) : null}
            </div>
          ) : null}

          {errors?.acceptedPaymentMethods ? (
            <div className="pt-2">
              <Form.Error>{errors.acceptedPaymentMethods.message}</Form.Error>
            </div>
          ) : null}
          {errors?.applySurcharge ? (
            <div className="pt-2">
              <Form.Error>{errors.applySurcharge.message}</Form.Error>
            </div>
          ) : null}
        </div>
        <div className="flex flex-col pt-4">
          <Form.Label htmlFor="payment-methods">Other Methods</Form.Label>
          <Form.Checkbox
            {...register("allowDirectPayment")}
            compact
            label="Settle directly with marina (Cash, Check)"
          />
        </div>
      </div>

      <PageActions
        pageValidation={() => trigger(VALIDATED_FIELDS_BY_TAB.rate)}
      />
    </>
  )
}

export default PaymentMethods
